import { StrictMode, type ReactElement } from 'react'
import { createRoot } from 'react-dom/client'
import './tokens/index.css'
import './i18n'
import { ligarSentry } from './observabilidade/sentry'
import App from './App.tsx'
import { ProvedorTema } from './tema/ProvedorTema.tsx'
import { ProvedorSessao } from './auth/ProvedorSessao.tsx'
import { Protegido } from './auth/Protegido.tsx'
import { Casca } from './componentes/Casca.tsx'
import { ContextoConversa } from './embed/ContextoConversa'
import { SessaoDoPainel } from './embed/SessaoDoPainel'
import { AssistenciaConversa } from './embed/AssistenciaConversa'
import { EnviarArquivoPainel } from './embed/EnviarArquivoPainel'
import { Ajuda } from './paginas/Ajuda'
import { Conhecimento } from './paginas/Conhecimento'
import { Console } from './paginas/Console'
import { BancoDeArquivos } from './paginas/BancoDeArquivos'
import { Publicacao } from './paginas/Publicacao'
import { Entregas } from './paginas/Entregas'
import { Materiais } from './paginas/Materiais'
import { Painel } from './paginas/Painel'
import { Regras } from './paginas/Regras'
import { Roteamento } from './paginas/Roteamento'
import { TabelaDePrecos } from './paginas/TabelaDePrecos'
import { HistoricoConversa } from './embed/HistoricoConversa.tsx'
import { Catalogo } from './paginas/Catalogo.tsx'
import { Contatos } from './paginas/Contatos.tsx'
import { Cotacao } from './paginas/Cotacao.tsx'
import { Entrar } from './paginas/Entrar.tsx'
import { RecuperarSenha } from './paginas/RecuperarSenha.tsx'
import { DefinirSenha } from './paginas/DefinirSenha.tsx'
import { Marcas } from './paginas/Marcas.tsx'
import { Times } from './paginas/Times.tsx'
import { ConfiguracaoIa } from './paginas/ConfiguracaoIa.tsx'
import { Transportadoras } from './paginas/Transportadoras.tsx'
import { Prazos } from './paginas/Prazos'
import { Embalagens } from './paginas/Embalagens.tsx'
import { Usuarios } from './paginas/Usuarios.tsx'
import { ConfiguracaoSmtp } from './paginas/ConfiguracaoSmtp.tsx'
import { ModelosDeWhatsapp } from './paginas/ModelosDeWhatsapp.tsx'
import { EnviarModelo } from './paginas/EnviarModelo.tsx'
import { ConsumoDeIa } from './paginas/ConsumoDeIa.tsx'
import { Assistentes } from './paginas/Assistentes.tsx'
import { Caixas } from './paginas/Caixas.tsx'
import { Simulacao } from './paginas/Simulacao.tsx'
import { Orcamentos } from './paginas/Orcamentos.tsx'
import { LogDeEntrega } from './paginas/LogDeEntrega.tsx'
import { Avaliacoes } from './paginas/Avaliacoes.tsx'

// Roteamento mínimo por caminho. As telas de `embed` são carregadas pelo
// Chatwoot dentro de um iframe e não devem herdar nada do portal — nem menu,
// nem cabeçalho. Quando o portal ganhar mais telas, entra um router de verdade.
const caminho = window.location.pathname

/**
 * Telas que existem antes da sessão. Ficam FORA da guarda de propósito: o link
 * do convite chega por e-mail para quem ainda não tem senha, e mandá-lo para a
 * tela de entrada seria um beco sem saída.
 */
const anonimas: Record<string, () => ReactElement> = {
  '/entrar': () => <Entrar />,
  '/esqueci-a-senha': () => <RecuperarSenha />,
  '/definir-senha': () => <DefinirSenha convite />,
  '/redefinir-senha': () => <DefinirSenha convite={false} />,
}

function rota() {
  // A sessão do painel embutido vem do token de instalação + o agente logado no
  // Chatwoot — nunca de formulário de senha dentro do iframe, que é a forma
  // clássica do phishing.
  if (caminho.startsWith('/embed/historico'))
    return <SessaoDoPainel painel="acervo">{() => <HistoricoConversa />}</SessaoDoPainel>

  if (caminho.startsWith('/embed/contexto'))
    return <SessaoDoPainel painel="contexto">{() => <ContextoConversa />}</SessaoDoPainel>

  // 🔴 Aba PRÓPRIA, com escopo próprio (demanda 057). Contexto é referência;
  // assistência é ação. E o `painel` que vai na sessão decide quais permissões o
  // servidor devolve — não é rótulo.
  if (caminho.startsWith('/embed/assistencia'))
    return <SessaoDoPainel painel="assistencia">{() => <AssistenciaConversa />}</SessaoDoPainel>

  // ⛔ **A exceção à regra acima, decidida pelo Ferrari em 28/09 (demanda 282, A+).** A
  // aba "Enviar arquivo" fala com o cliente, então NÃO abre sessão embutida: exige a do
  // PORTAL, e o login dela é feito aqui dentro, uma vez por jornada — em vez da aba nova
  // da 277. O painel mostra de quem é o login antes de pedir a senha.
  if (caminho.startsWith('/embed/enviar-arquivo')) return <EnviarArquivoPainel />

  const anonima = anonimas[caminho]
  if (anonima) return anonima()

  // O catálogo é material de desenvolvimento e não mostra dado de ninguém.
  if (caminho.startsWith('/catalogo')) return <Catalogo />

  // 🔴 **A mesma tela do painel, agora alcançável SEM conversa.**
  //
  // O painel embutido é um `dashboard app`, e o Chatwoot só o monta ao lado de
  // uma conversa. Depois da migração de 05/09, 4.217 contatos ganharam
  // histórico e nenhuma porta: conversa eles só terão quando escreverem de
  // novo. Aqui o atendente busca por nome ou telefone e lê o mesmo acervo, com
  // a sessão do próprio portal.
  //
  // ⭐ É o MESMO componente, com uma diferença: avulso ele não espera o
  // `postMessage` que ninguém vai mandar. `?telefone=` já é entendido pelo
  // contexto, então o link do contato no Chatwoot cai direto na pessoa certa.
  if (caminho.startsWith('/acervo'))
    return (
      <Protegido exige="AcervoVer">
        <Casca>
          <HistoricoConversa avulso />
        </Casca>
      </Protegido>
    )

  // 🔴 **Tela do PORTAL, e não do painel embutido** (demanda 209). O polegar
  // vive no iframe e fala da conversa aberta ao lado; ler o julgamento da equipe
  // inteira é outra coisa, e a sessão do iframe não a autoriza.
  // ⛔ **Tela do PORTAL, aberta pelo painel numa aba** (demanda 277 · B2). Enviar ao
  // cliente exige o login do portal (2FA para quem o tem ativo) — a sessão do iframe
  // serve para ler. ⚠️ O cookie é o mesmo das duas sessões: quem chega aqui pelo link do
  // Chatwoot chega com a sessão EMBUTIDA, e o `Protegido` oferece o login.
  if (caminho.startsWith('/enviar-modelo'))
    return (
      <Protegido exige="ModeloEnviar">
        <Casca>
          <EnviarModelo />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/avaliacoes'))
    return (
      <Protegido exige="AvaliacaoVer">
        <Casca>
          <Avaliacoes />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/usuarios'))
    return (
      <Protegido exige="UsuarioVer">
        <Casca>
          <Usuarios />
        </Casca>
      </Protegido>
    )

  if (/^\/marcas\/[^/]+\/simulacao/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Simulacao />
        </Casca>
      </Protegido>
    )

  // A cotação pelos três motores. Vem antes de `/orcamentos` na ordem só por
  // clareza: os caminhos não se sobrepõem.
  if (/^\/marcas\/[^/]+\/cotacao/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Cotacao />
        </Casca>
      </Protegido>
    )

  if (/^\/marcas\/[^/]+\/orcamentos/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Orcamentos />
        </Casca>
      </Protegido>
    )

  // /marcas/:marca/caixas
  if (/^\/marcas\/[^/]+\/caixas/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Caixas />
        </Casca>
      </Protegido>
    )

  // /marcas/:marca/assistentes
  if (/^\/marcas\/[^/]+\/assistentes/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Assistentes />
        </Casca>
      </Protegido>
    )

  // Antes de `conhecimento` e das demais: `startsWith` não é usado aqui, mas a
  // ordem entre regexes de marca importa para quem vier depois.
  if (/^\/marcas\/[^/]+\/materiais/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Materiais />
        </Casca>
      </Protegido>
    )

  // Permissão própria, e diferente da das entregas: material institucional não
  // tem titular, e o backlog exige política distinta da dos documentos gerados.
  if (/^\/marcas\/[^/]+\/arquivos/.test(caminho))
    return (
      <Protegido exige="ArquivoVer">
        <Casca>
          <BancoDeArquivos />
        </Casca>
      </Protegido>
    )

  // 🔴 **Vem ANTES de `/entregas`, e a ordem é obrigatória.**
  //
  // O teste é `startsWith` disfarçado de regex sem `$`: `/entregas-de-documento`
  // casa `^/marcas/[^/]+/entregas` e cairia na tela do comprovante físico. Duas
  // telas com nomes parecidos, e a errada abrindo sem erro nenhum.
  if (/^\/marcas\/[^/]+\/entregas-de-documento/.test(caminho))
    return (
      <Protegido exige="AuditoriaVer">
        <Casca>
          <LogDeEntrega />
        </Casca>
      </Protegido>
    )

  // Permissão própria: registro de entrega tem assinatura e, com frequência, o
  // documento de quem recebeu. Quem barra de verdade é o servidor.
  if (/^\/marcas\/[^/]+\/entregas/.test(caminho))
    return (
      <Protegido exige="EntregaVer">
        <Casca>
          <Entregas />
        </Casca>
      </Protegido>
    )

  if (/^\/marcas\/[^/]+\/console/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Console />
        </Casca>
      </Protegido>
    )

  if (/^\/marcas\/[^/]+\/conhecimento/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Conhecimento />
        </Casca>
      </Protegido>
    )

  if (/^\/marcas\/[^/]+\/roteamento/.test(caminho))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Roteamento />
        </Casca>
      </Protegido>
    )

  // Depois das rotas `/marcas/:marca/...`: `/marcas` sozinho é o cadastro, e um
  // `startsWith` antes delas engoliria todas.
  if (caminho === '/marcas' || caminho === '/marcas/')
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Marcas />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/configuracao/publicacao'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Publicacao />
        </Casca>
      </Protegido>
    )

  // ⚠️ **Fora de `/marcas/…`, e de propósito.** A caixa em que o material viaja
  // é da operação, não da marca: a Di Giorgio e a Letras e Versos despacham do
  // mesmo endereço, no mesmo tipo de embalagem.
  if (caminho.startsWith('/configuracao/embalagens'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Embalagens />
        </Casca>
      </Protegido>
    )

  // ⚠️ Fora de `/marcas/…` pelo mesmo motivo da embalagem: o prazo de
  // fabricação é da FÁBRICA, e a fábrica é uma só para as duas marcas. O que
  // varia é a família de produto, não quem vende.
  if (caminho.startsWith('/configuracao/prazos'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Prazos />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/configuracao/transporte'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Transportadoras />
        </Casca>
      </Protegido>
    )

  // Demanda 231: os modelos de mensagem do WhatsApp. Ver é `ParametroVer`;
  // criar e apagar exigem `ParametroGerenciar`, e quem barra é o servidor.
  if (caminho.startsWith('/configuracao/whatsapp'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <ModelosDeWhatsapp />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/configuracao/ia'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <ConfiguracaoIa />
        </Casca>
      </Protegido>
    )

  // As oito decisões do cliente. `ParametroVer` para abrir; gravar exige
  // `ParametroGerenciar`, e quem barra é o servidor.
  // Contato único: ver os candidatos é `AcervoVer`; unir exige
  // `ParametroGerenciar`, e quem barra é o servidor.
  // Sem `exige`: ajuda protegida por permissão não serve a treinamento. Um
  // atendente novo precisa poder descobrir o que existe antes de ter acesso.
  if (caminho.startsWith('/ajuda'))
    return (
      <Protegido>
        <Casca>
          <Ajuda />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/contatos'))
    return (
      <Protegido exige="AcervoVer">
        <Casca>
          <Contatos />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/regras'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Regras />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/times'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Times />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/painel'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <Painel />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/tabela-de-precos'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <TabelaDePrecos />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/ia/consumo'))
    return (
      <Protegido exige="ParametroVer">
        <Casca>
          <ConsumoDeIa />
        </Casca>
      </Protegido>
    )

  if (caminho.startsWith('/configuracao/email'))
    return (
      <Protegido exige="SmtpGerenciar">
        <Casca>
          <ConfiguracaoSmtp />
        </Casca>
      </Protegido>
    )

  return (
    <Protegido>
      <Casca>
        <App />
      </Casca>
    </Protegido>
  )
}

// Antes de qualquer render: erro que acontece montando a árvore também é erro,
// e ligar depois deixaria justamente o pior caso de fora (demanda 012).
ligarSentry()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ProvedorTema>
      <ProvedorSessao>{rota()}</ProvedorSessao>
    </ProvedorTema>
  </StrictMode>,
)
